/**
 * Captures the credential this app passes through the address bar
 * (`/verify-email?token=`), then strips it out, so nothing that reads
 * `window.location` afterwards can observe it (86bc02ugy).
 *
 * **Why this rather than a Sentry hook.** The first attempt scrubbed the `href`
 * on Replay's rrweb meta event from `beforeAddRecordingEvent`. That hook is
 * never called for a meta event: Replay applies it through `maybeApplyCallback`,
 * guarded by `isCustomEvent(event)` — `event.type === EventType.Custom` (5). A
 * meta event is `EventType.Meta` (4), so the callback is skipped and the event
 * is buffered verbatim. Dev QA kept finding live credentials in stored replay
 * metadata while unit tests calling the scrubber directly passed.
 * `ReplayEvent.urls[]` is out of reach of every hook as well. Emptying the
 * address bar before Sentry starts fixes both.
 *
 * This is a copy of libs/frontend-utils/src/url-credentials.ts, which
 * react-scripts cannot compile from libs/ until the Vite migration — the same
 * arrangement as utils/scrub-url.util.ts. Keep the two in sync.
 */

/** Query parameters whose values are credentials. */
const CREDENTIAL_SEARCH_PARAMS = ['code', 'token'];

/** Fragment parameters whose values are credentials. */
const CREDENTIAL_HASH_PARAMS = ['mercury_token'];

let captured = false;
let capturedSearch = '';
let capturedHash = '';

const stripLeadingHash = (hash: string): string =>
  hash.startsWith('#') ? hash.slice(1) : hash;

/**
 * A parameter counts as a credential only when some occurrence carries a value.
 *
 * `getAll`, not `get`: `get` returns the first value only, so
 * `?code=&code=SECRET` read as empty and the URL was left intact for Replay to
 * snapshot. Not `has` either: a valueless `#mercury_token=` is not an SSO
 * return, so it must not trigger a rewrite.
 */
const hasCredential = (
  params: URLSearchParams,
  names: readonly string[]
): boolean => names.some((name) => params.getAll(name).some(Boolean));

/**
 * Snapshot `search` and `hash`, then rewrite the address bar without the
 * credential-bearing parts. Idempotent: the first call wins.
 *
 * Non-credential parameters survive.
 */
export function captureAndStripUrlCredentials(): void {
  if (captured || typeof window === 'undefined') return;
  captured = true;

  // Defaults, not assumptions: a test double or a non-browser host can leave
  // these undefined, and this runs during bootstrap where a throw would take
  // the whole app down rather than just telemetry.
  const { search = '', hash = '', pathname = '' } = window.location;
  capturedSearch = search;
  capturedHash = hash;

  const searchParams = new URLSearchParams(search);
  const hashParams = new URLSearchParams(stripLeadingHash(hash));

  const searchHasCredential = hasCredential(
    searchParams,
    CREDENTIAL_SEARCH_PARAMS
  );
  const hashHasCredential = hasCredential(hashParams, CREDENTIAL_HASH_PARAMS);
  if (!searchHasCredential && !hashHasCredential) return;

  for (const name of CREDENTIAL_SEARCH_PARAMS) searchParams.delete(name);
  const nextSearch = searchParams.toString();

  window.history?.replaceState?.(
    null,
    '',
    nextSearch ? `${pathname}?${nextSearch}` : pathname
  );
}

/**
 * The query string as it was on load, credentials included. Captures on first
 * use, so a reader that runs before Sentry init still sees the real URL.
 */
export function capturedSearchParams(): URLSearchParams {
  captureAndStripUrlCredentials();
  return new URLSearchParams(capturedSearch);
}

/** The fragment as it was on load, credentials included. */
export function capturedHashParams(): URLSearchParams {
  captureAndStripUrlCredentials();
  return new URLSearchParams(stripLeadingHash(capturedHash));
}

/** Test seam: forget the capture so a spec can set up a fresh location. */
export function resetCapturedUrlCredentialsForTests(): void {
  captured = false;
  capturedSearch = '';
  capturedHash = '';
}
